import {
  activeReviews,
  dayKey,
  shiftDay,
  lemmaKey,
  deviceZone,
  type Snapshot,
  type ReviewEvent,
  type WordSense,
} from "./domain";
import { difficultWords } from "./study";
export const ratio = (n: number, d: number) => (d ? Math.round((n / d) * 100) : null);
export const pct = (n: number | null) => (n === null ? "—" : n + "%");
export function performance(events: readonly ReviewEvent[]) {
  const success = (e: ReviewEvent) =>
    e.assessment === "automatic" ? e.correct === true : e.effectiveRating >= 2;
  const automatic = events.filter((e) => e.assessment === "automatic");
  const self = events.filter((e) => e.assessment === "self");
  const unaided = events.filter((e) => !e.hint);
  const aided = events.filter((e) => e.hint);
  const predicted = self.filter((e) => e.prediction !== "unrecorded");
  const unaidedAuto = unaided.filter((e) => e.assessment === "automatic"),
    unaidedSelf = unaided.filter((e) => e.assessment === "self");
  const aidedAuto = aided.filter((e) => e.assessment === "automatic"),
    aidedSelf = aided.filter((e) => e.assessment === "self");
  return {
    count: events.length,
    automaticN: automatic.length,
    automaticCorrect: automatic.filter((e) => e.correct).length,
    automaticRate: ratio(automatic.filter((e) => e.correct).length, automatic.length),
    selfN: self.length,
    selfRecalled: self.filter(success).length,
    selfRate: ratio(self.filter(success).length, self.length),
    firstRate: ratio(
      predicted.filter((e) => e.prediction === "remembered").length,
      predicted.length,
    ),
    firstN: predicted.length,
    unaidedRate: ratio(unaidedAuto.filter(success).length, unaidedAuto.length),
    unaidedN: unaidedAuto.length,
    aidedRate: ratio(aidedAuto.filter(success).length, aidedAuto.length),
    aidedN: aidedAuto.length,
    unaidedSelfRate: ratio(unaidedSelf.filter(success).length, unaidedSelf.length),
    unaidedSelfN: unaidedSelf.length,
    aidedSelfRate: ratio(aidedSelf.filter(success).length, aidedSelf.length),
    aidedSelfN: aidedSelf.length,
    failures: events.filter((e) => e.effectiveRating === 1).length,
    ratings: [1, 2, 3, 4].map((n) => events.filter((e) => e.rating === n).length),
    activeMs: events.reduce((sum, e) => sum + e.activeMs, 0),
  };
}
export function analytics(s: Snapshot, range: number = 30, now = new Date()) {
  const zone = deviceZone(),
    dates = new Map<string, string>();
  const localDay = (at: string | Date) => {
    const key = typeof at === "string" ? at : at.toISOString();
    if (!dates.has(key)) dates.set(key, dayKey(at, zone));
    return dates.get(key)!;
  };
  const today = localDay(now),
    all = activeReviews(s.events),
    from = range ? shiftDay(today, -range + 1) : "0000-00-00";
  // Re-bucket all instants into the current device zone; never slice UTC dates for daily charts.
  const filtered = all.filter((e) => localDay(e.at) >= from && localDay(e.at) <= today);
  const words = s.words.filter((w) => w.active && !w.deleted),
    wordMap = new Map(s.words.map((w) => [w.id, w]));
  const activeWordIds = new Set(words.map((w) => w.id)),
    cards = s.cards.filter(
      (c) => activeWordIds.has(c.wordId) && c.direction !== "collocation",
    );
  const cardIds = new Set(cards.map((c) => c.id)),
    states = s.states.filter((st) => cardIds.has(st.cardId));
  const cardMap = new Map(cards.map((c) => [c.id, c]));
  const introduced = new Set(all.map((e) => e.wordId));
  const firstDays = new Map<string, string>();
  for (const e of all) {
    const w = wordMap.get(e.wordId);
    if (w) {
      const key = lemmaKey(w),
        date = localDay(e.at);
      if (!firstDays.has(key) || date < firstDays.get(key)!) firstDays.set(key, date);
    }
  }
  const due = states.filter(
    (v) => v.memory.reps > 0 && Date.parse(v.memory.dueAt) <= now.getTime(),
  );
  const overdue = due.filter((v) => localDay(v.memory.dueAt) < today);
  const learningIds = new Set(
    states
      .filter((v) => [1, 3].includes(v.memory.state))
      .map((v) => cardMap.get(v.cardId)!.wordId),
  );
  const strongIds = new Set(
    states
      .filter(
        (v) =>
          v.memory.stability >= 21 &&
          v.memory.difficulty <= 5 &&
          v.memory.reps >= 3 &&
          v.memory.state === 2,
      )
      .map((v) => cardMap.get(v.cardId)!.wordId),
  );
  const grouped = new Map<string, ReviewEvent[]>();
  for (const e of filtered) {
    const list = grouped.get(e.wordId) ?? [];
    list.push(e);
    grouped.set(e.wordId, list);
  }
  const rank = [...grouped]
    .map(([wordId, es]) => {
      es.sort((a, b) => a.at.localeCompare(b.at));
      let streak = 0;
      for (let i = es.length - 1; i >= 0 && es[i].effectiveRating === 1; i--) streak++;
      const recent = es.slice(-3),
        previous = es.slice(-6, -3);
      const average = (v: ReviewEvent[]) =>
        v.reduce((n, e) => n + e.effectiveRating, 0) / v.length;
      return {
        word: wordMap.get(wordId) as WordSense | undefined,
        total: es.length,
        forgot: es.filter((e) => e.effectiveRating === 1).length,
        hints: es.filter((e) => e.hint).length,
        avgMs: es.reduce((n, e) => n + e.answerMs, 0) / es.length,
        streak,
        declining:
          recent.length === 3 &&
          previous.length === 3 &&
          average(recent) < average(previous),
        difficultyRise:
          es.at(-1)!.after.memory.difficulty - es[0].before.memory.difficulty,
      };
    })
    .filter((r) => r.word && !r.word.deleted);
  const dailyMap = new Map<
    string,
    { date: string; count: number; minutes: number; newWords: number }
  >();
  const chartDays =
    range ||
    Math.max(
      1,
      Math.min(
        366,
        all.length
          ? Math.ceil(
              (new Date(today + "T12:00:00Z").getTime() -
                new Date(
                  localDay(all.reduce((a, b) => (a.at < b.at ? a : b)).at) +
                    "T12:00:00Z",
                ).getTime()) /
                86400000,
            ) + 1
          : 30,
      ),
    );
  for (let i = chartDays - 1; i >= 0; i--) {
    const date = shiftDay(today, -i);
    dailyMap.set(date, {
      date,
      count: 0,
      minutes: 0,
      newWords: [...firstDays.values()].filter((d) => d === date).length,
    });
  }
  for (const e of all) {
    const day = dailyMap.get(localDay(e.at));
    if (day) {
      day.count++;
      day.minutes += e.activeMs / 60000;
    }
  }
  const activeDays = [...new Set(all.map((e) => localDay(e.at)))].sort();
  let longest = 0,
    run = 0,
    previousDay = "";
  for (const d of activeDays) {
    run = shiftDay(previousDay || d, 1) === d ? run + 1 : 1;
    longest = Math.max(longest, run);
    previousDay = d;
  }
  const activity = new Set(activeDays);
  let cursor = activity.has(today) ? today : shiftDay(today, -1),
    current = 0;
  while (activity.has(cursor)) {
    current++;
    cursor = shiftDay(cursor, -1);
  }
  const forecast = Array.from({ length: 30 }, (_, i) => {
    const date = shiftDay(today, i);
    return {
      date,
      count: states.filter(
        (v) =>
          v.memory.reps > 0 &&
          (i === 0
            ? Date.parse(v.memory.dueAt) <= now.getTime() ||
              localDay(v.memory.dueAt) === today
            : localDay(v.memory.dueAt) === date),
      ).length,
    };
  });
  const unique = (ws: WordSense[]) => new Set(ws.map(lemmaKey)).size;
  return {
    all,
    filtered,
    perf: performance(filtered),
    last7: performance(all.filter((e) => localDay(e.at) >= shiftDay(today, -6))),
    last30: performance(all.filter((e) => localDay(e.at) >= shiftDay(today, -29))),
    words,
    uniqueWords: unique(words.filter((w) => w.kind === "word")),
    uniquePhrases: unique(words.filter((w) => w.kind === "phrase")),
    senses: words.length,
    introduced: unique(words.filter((w) => introduced.has(w.id))),
    newToday: [...firstDays.values()].filter((d) => d === today).length,
    newInRange: [...firstDays.values()].filter((d) => d >= from && d <= today).length,
    learning: unique(words.filter((w) => learningIds.has(w.id))),
    strong: unique(words.filter((w) => strongIds.has(w.id))),
    difficult: difficultWords(s),
    due,
    overdue,
    daily: [...dailyMap.values()],
    activeDays: activeDays.length,
    longestStreak: longest,
    currentStreak: current,
    forecast,
    rank,
    todayMs: all
      .filter((e) => localDay(e.at) === today)
      .reduce((n, e) => n + e.activeMs, 0),
    weekMs: all
      .filter(
        (e) =>
          localDay(e.at) >=
            shiftDay(today, -((new Date(today + "T12:00:00Z").getUTCDay() + 6) % 7)) &&
          localDay(e.at) <= today,
      )
      .reduce((n, e) => n + e.activeMs, 0),
    reviews: all.filter((e) => e.before.memory.reps > 0).length,
    answered: all.length,
    totalMs: all.reduce((n, e) => n + e.activeMs, 0),
    goalRate: ratio(
      Math.min(
        [...firstDays.values()].filter((d) => d === today).length,
        s.settings.dailyNew,
      ),
      s.settings.dailyNew,
    ),
  };
}
